import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { buildCartInfoVariables } from '@features/cart/hooks/useCartInfo'
import { useCartStore } from '@features/cart/model/useCartStore'
import { useAddressStore } from '@features/shipping-methods'

import { useAnalytics } from '@shared/lib/analytics'

export interface UsePromocodeManagerReturn {
  appliedPromocode: string;
  isApplied: boolean;
  saleAmount: number;
  isProcessing: boolean;
  applyPromocode: (code: string) => Promise<void>;
  clearPromocode: () => Promise<void>;
}

function getPromocodeSaleErrorMessage (
  cartInfoData: ReturnType<typeof useCartStore.getState>['cartInfoData'],
  promocode: string,
) {
  const normalizedCode = promocode.trim().toUpperCase()
  const saleErrors = cartInfoData?.saleErrors ?? []

  const matchingError = saleErrors.find((error) => {
    const errorCode = String(
      error?.inputPart?.promocodes?.code ?? '',
    ).trim().toUpperCase()

    return !!error?.message && errorCode === normalizedCode
  })

  return matchingError?.message?.trim() || ''
}

export function usePromocodeManager (): UsePromocodeManagerReturn {
  const { t } = useTranslation('cart')
  const { shopId, zoneId, deliveryType, selectedAddress } = useAddressStore()
  const currentShopId = useCartStore((state) => state.currentShopId)
  const cart = useCartStore((state) =>
    state.currentShopId ? state.carts[state.currentShopId] : undefined,
  )
  const cartInfoData = useCartStore((state) => state.cartInfoData)
  const setPromocode = useCartStore((state) => state.setPromocode)
  const getCartInfoData = useCartStore((state) => state.getCartInfoData)
  const { applyPromoCode: trackPromoCode } = useAnalytics()

  const [ isProcessing, setIsProcessing ] = useState(false)
  const operationRef = useRef(0)

  const appliedPromocode = useMemo(() => {
    return String(
      cartInfoData?.promocode || cart?.promocode || '',
    ).trim().toUpperCase()
  }, [ cart?.promocode, cartInfoData?.promocode ])

  const saleAmount = useMemo(
    () => Number(cartInfoData?.totals?.sale ?? 0),
    [ cartInfoData?.totals?.sale ],
  )

  const refreshCartInfo = useCallback(async (
    promocode?: string,
    options?: { clearPromocode?: boolean },
  ) => {
    const variables = buildCartInfoVariables({
      shopId,
      zoneId,
      deliveryType,
      customerGeoPoint: selectedAddress?.customerGeoPoint,
      clearPromocode: options?.clearPromocode,
      cart: currentShopId
        ? {
          items: cart?.items ?? [],
          promocode,
        }
        : undefined,
    })

    if (variables) {
      await getCartInfoData(variables)
    }

    return useCartStore.getState().cartInfoData
  }, [
    cart?.items,
    currentShopId,
    deliveryType,
    getCartInfoData,
    selectedAddress?.customerGeoPoint,
    shopId,
    zoneId,
  ])

  const applyPromocode = useCallback(async (code: string) => {
    const normalizedCode = code.trim().toUpperCase()

    if (!normalizedCode) {
      throw new Error(t('discount.enter_promocode'))
    }

    const operationId = operationRef.current + 1
    operationRef.current = operationId
    setIsProcessing(true)

    try {
      setPromocode(normalizedCode)
      const refreshedCartInfo = await refreshCartInfo(normalizedCode)

      if (operationRef.current !== operationId) {
        return
      }

      const saleErrorMessage = getPromocodeSaleErrorMessage(
        refreshedCartInfo,
        normalizedCode,
      )

      if (saleErrorMessage) {
        setPromocode('')
        trackPromoCode(normalizedCode, false)
        throw new Error(saleErrorMessage)
      }

      const appliedCode = String(refreshedCartInfo?.promocode || '').trim().toUpperCase()
      const discount = Number(refreshedCartInfo?.totals?.sale ?? 0)

      if (appliedCode !== normalizedCode) {
        setPromocode('')
        trackPromoCode(normalizedCode, false)
        throw new Error(t('discount.invalid_promocode'))
      }

      trackPromoCode(normalizedCode, true, discount)
    }
    catch (error) {
      if (!(error instanceof Error)) {
        trackPromoCode(normalizedCode, false)
        throw new Error(t('discount.apply_error'))
      }

      throw error
    }
    finally {
      if (operationRef.current === operationId) {
        setIsProcessing(false)
      }
    }
  }, [ refreshCartInfo, setPromocode, t, trackPromoCode ])

  const clearPromocode = useCallback(async () => {
    const operationId = operationRef.current + 1
    operationRef.current = operationId
    setIsProcessing(true)

    try {
      setPromocode('')
      const refreshedCartInfo = await refreshCartInfo(undefined, {
        clearPromocode: true,
      })

      const appliedCode = String(refreshedCartInfo?.promocode || '').trim()

      if (appliedCode) {
        setPromocode(appliedCode)
        throw new Error(t('discount.clear_error'))
      }
    }
    catch {
      throw new Error(t('discount.clear_error'))
    }
    finally {
      if (operationRef.current === operationId) {
        setIsProcessing(false)
      }
    }
  }, [ refreshCartInfo, setPromocode, t ])

  return {
    appliedPromocode,
    isApplied: Boolean(appliedPromocode),
    saleAmount,
    isProcessing,
    applyPromocode,
    clearPromocode,
  }
}
